// utils/diagnostics.js
// Admin-only buoy diagnostics, rendered as color-coded chips on each buoy tile
// (/ponds). See the pond-diagnostics design handoff (chip layout + tones).

// Chip tones: background + label color + value color.
export const DIAG_TONES = {
  default: { bg: '#efeae0', label: '#8a8579', val: '#4a4a42' },
  red:     { bg: '#f8e7e3', label: '#b07b74', val: '#c0463d' },
  green:   { bg: '#e7f4ec', label: '#71997f', val: '#1c7d45' },
  yellow:  { bg: '#f9f1cc', label: '#9a8b45', val: '#a8880c' },
  lorange: { bg: '#fbf0da', label: '#a58a55', val: '#c1841f' },
  dorange: { bg: '#f6e1cf', label: '#a5794f', val: '#b3551a' },
};

// Bottom DO (6h avg) severity by value: ≤2 red, ≤3 yellow, else neutral.
const bottomDoTone = (v) => {
  if (v == null) return 'default';
  if (v <= 2) return 'red';
  if (v <= 3) return 'yellow';
  return 'default';
};

// Named HAB risk → chip tone.
const HAB_TONE = { baseline: 'green', elevated: 'lorange', advisory: 'dorange', warning: 'red' };

// Chips shown to non-admin users (admins see the full set). Matched by label.
export const NON_ADMIN_DIAG_LABELS = ['HAB risk', 'Bottom DO (6h avg)'];

// Alarm conditions that both red-tint a chip AND escalate the grid to
// "Needs attention". Single source of truth for chip tone + escalation.
export const DIAG_ALARM_FIELDS = [
  'battery_voltage', 'gas_baro_humidity', 'pump1_rpm_at_dc29', 'pump2_rpm_at_dc50',
];
export const isDiagFieldAlarm = (key, v) => {
  if (v == null) return false;
  if (key === 'battery_voltage') return v < 12.9;
  if (key === 'gas_baro_humidity') return v > 75;
  if (key === 'pump1_rpm_at_dc29' || key === 'pump2_rpm_at_dc50') return v > 3000 || v < 1000;
  return false;
};

export const tileHasAlarm = (tile) =>
  !!tile && DIAG_ALARM_FIELDS.some(k => isDiagFieldAlarm(k, tile[k]));

const round2 = (v) => (typeof v === 'number' ? Number(v.toFixed(2)) : v);
const fmt = (v, unit) => (v == null ? '—' : (unit ? `${round2(v)} ${unit}` : String(round2(v))));

const fmtRiskTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtAlarm = (v) => {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

// Ordered chips for one diagnostics tile → [{ label, value, tone }]. Design
// field names are mapped to the actual /grids/<id>/diagnostics/ payload keys.
export const buildDiagChips = (tile) => {
  if (!tile) return [];
  const t = tile;
  const habName = t.hab_risk_label ? String(t.hab_risk_label).toLowerCase() : null;
  const alarmTone = (key) => (isDiagFieldAlarm(key, t[key]) ? 'red' : 'default');
  return [
    { label: 'Battery', value: t.battery_voltage == null ? '—' : `${t.battery_voltage.toFixed(2)} V`, tone: alarmTone('battery_voltage') },
    { label: 'Baro humidity', value: t.gas_baro_humidity == null ? '—' : String(Number(t.gas_baro_humidity.toFixed(1))), tone: alarmTone('gas_baro_humidity') },
    { label: 'Pump 1 RPM', value: fmt(t.pump1_rpm_at_dc29, 'rpm'), tone: alarmTone('pump1_rpm_at_dc29') },
    { label: 'Pump 2 RPM', value: fmt(t.pump2_rpm_at_dc50, 'rpm'), tone: alarmTone('pump2_rpm_at_dc50') },
    { label: 'Baro temp', value: fmt(t.gas_baro_temperature, '°F'), tone: 'default' },
    { label: 'Water O₂ (RDO)', value: fmt(t.water_o2_rdo, 'mg/L'), tone: 'default' },
    { label: 'Most Recent Phycocyanin', value: fmt(t.pc_fluorometer, 'µg/L'), tone: 'default' },
    { label: 'HAB risk tier', value: t.hab_risk_tier == null ? '—' : String(t.hab_risk_tier), tone: 'default' },
    { label: 'HAB risk', value: t.hab_risk_label ? String(t.hab_risk_label).toUpperCase() : '—', tone: HAB_TONE[habName] || 'default' },
    { label: 'HAB risk time', value: fmtRiskTime(t.hab_risk_time), tone: 'default' },
    { label: 'Bottom DO (6h avg)', value: fmt(t.bottom_do_6h_avg, 'mg/L'), tone: bottomDoTone(t.bottom_do_6h_avg) },
    { label: 'Bottom DO depth', value: fmt(t.bottom_do_depth, 'm'), tone: 'default' },
    { label: 'Bottom DO alarm', value: fmtAlarm(t.bottom_do_alarm), tone: 'default' },
  ];
};
